import { toast } from 'sonner';
import { useTaskStore } from '@/stores/taskStore';
import { useUIStore } from '@/stores/uiStore';
import { useHabitDetail } from '@/features/habits/useHabitDetail';
import type { AiAction, Task } from '@/types';

export type UndoFn = () => void;

// Defense against AI year-drift (claude-sonnet-4-6 has been observed returning
// prior-year timestamps despite explicit Now anchors). If the AI's ms lands in
// a past year, bump it forward to the current year while preserving month/day/hour/minute.
function correctYearDrift(ms: number): number {
  const d = new Date(ms);
  const currentYear = new Date().getFullYear();
  if (d.getFullYear() < currentYear) {
    d.setFullYear(currentYear);
    return d.getTime();
  }
  return ms;
}

function snapshotTask(taskId: string): Task | undefined {
  return useTaskStore.getState().tasks.find((t) => t.id === taskId);
}

function restoreTask(snapshot: Task | undefined, originalId: string) {
  if (!snapshot) {
    useTaskStore.setState((s) => ({ tasks: s.tasks.filter((t) => t.id !== originalId) }));
    return;
  }
  useTaskStore.setState((s) => {
    const exists = s.tasks.some((t) => t.id === snapshot.id);
    return exists
      ? { tasks: s.tasks.map((t) => (t.id === snapshot.id ? snapshot : t)) }
      : { tasks: [...s.tasks, snapshot] };
  });
}

function emitToast(label: string, undo: UndoFn): void {
  toast.success(`${label} — done`, {
    duration: 5000,
    action: { label: 'Undo', onClick: undo },
  });
}

export function dispatch(action: AiAction, opts?: { silent?: boolean }): UndoFn {
  const store = useTaskStore.getState();
  let undo: UndoFn = () => undefined;
  let didMutate = false;

  switch (action.type) {
    case 'reschedule': {
      const before = snapshotTask(action.taskId);
      if (!before) break;
      const toMs = correctYearDrift(action.toMs);
      const start = new Date(toMs);
      const end = new Date(toMs + before.durationMinutes * 60_000);
      store.scheduleTask(before.id, start, end);
      undo = () => restoreTask(before, before.id);
      didMutate = true;
      break;
    }
    case 'reschedule_bulk': {
      const snapshots = action.taskIds
        .map((id) => snapshotTask(id))
        .filter((t): t is Task => Boolean(t));
      let cursorMs = correctYearDrift(action.toMs);
      for (const t of snapshots) {
        store.scheduleTask(
          t.id,
          new Date(cursorMs),
          new Date(cursorMs + t.durationMinutes * 60_000),
        );
        cursorMs += t.durationMinutes * 60_000;
      }
      undo = () => {
        for (const snap of snapshots) restoreTask(snap, snap.id);
      };
      didMutate = snapshots.length > 0;
      break;
    }
    case 'cancel_task': {
      const before = snapshotTask(action.taskId);
      if (!before) break;
      useTaskStore.setState((s) => ({ tasks: s.tasks.filter((t) => t.id !== before.id) }));
      undo = () => restoreTask(before, before.id);
      didMutate = true;
      break;
    }
    case 'create_task': {
      const startMs = action.startMs !== undefined ? correctYearDrift(action.startMs) : undefined;
      const created = store.addTask({
        title: action.title,
        priority: 'medium',
        energyRequired: 'medium',
        durationMinutes: action.durationMinutes,
        ...(startMs !== undefined
          ? {
              scheduledStart: new Date(startMs),
              scheduledEnd: new Date(startMs + action.durationMinutes * 60_000),
              status: 'scheduled' as const,
            }
          : {}),
      });
      undo = () =>
        useTaskStore.setState((s) => ({ tasks: s.tasks.filter((t) => t.id !== created.id) }));
      didMutate = true;
      break;
    }
    case 'compress_routine': {
      useUIStore.getState().openCompressionDialog(action.routineId);
      undo = () => useUIStore.getState().closeCompressionDialog();
      didMutate = true;
      break;
    }
    case 'create_habit': {
      // Per the chosen UX this opens the prefilled habit form for confirmation
      // rather than auto-adding. Close the AI palette so the form isn't stacked.
      useUIStore.getState().setCommandPaletteOpen(false);
      useHabitDetail.getState().openAdd({
        name: action.name,
        frequency: action.frequency,
        ...(action.customDays !== undefined ? { customDays: action.customDays } : {}),
        ...(action.scheduledTime !== undefined ? { scheduledTime: action.scheduledTime } : {}),
        ...(action.durationMinutes !== undefined
          ? { durationMinutes: action.durationMinutes }
          : {}),
      });
      undo = () => useHabitDetail.getState().close();
      // didMutate stays false → no bottom toast; the prefilled form is the confirmation.
      break;
    }
    case 'no_op': {
      undo = () => undefined;
      break;
    }
  }

  // Surfaces that show their own inline confirmation (the AI thread) pass
  // silent:true so we don't also stack a bottom toast on top of the sheet.
  if (didMutate && !opts?.silent) emitToast(action.label, undo);
  return undo;
}
